import styled from "@emotion/styled";
import type { MouseEvent, ReactNode, TouchEvent, TouchEventHandler } from "react";
import { useCallback, useRef, useState } from "react";

import { useTouch } from "../touch";
import { ClickShieldContext } from "./ClickShieldContext";
import { CLICK_SHIELD_TARGET } from "./constants";

type ClickOrTouchEvent = MouseEvent<HTMLDivElement> | TouchEvent;

const getClientCoords = (event: ClickOrTouchEvent) => {
  if ("changedTouches" in event) {
    const touch = event.changedTouches[0];
    return { clientX: touch.clientX, clientY: touch.clientY };
  }
  return { clientX: event.clientX, clientY: event.clientY };
};

const ClickShieldContainer = styled.div`
  position: relative;
  touch-action: none;

  height: 100%;
  width: 100%;
`;

const ClickShield = styled.div<{ foreground: boolean }>`
  position: absolute;
  top: 0;
  left: 0;

  height: 100%;
  width: 100%;

  touch-action: none;
  opacity: 0;

  /* note: allows for selection */
  z-index: ${({ foreground }) => (foreground ? 1 : -1)};
`;

export type ClickShieldProps = {
  onShieldClick?: () => void;
  onShieldDoubleClick?: () => void;
  children?: ReactNode;
};

export const ClickShieldProvider = ({ onShieldClick, onShieldDoubleClick, children }: ClickShieldProps) => {
  const active = useRef(true);
  const [foreground, setForeground] = useState(true);

  const activate = () => {
    active.current = true;
    setForeground(true);
  };

  const deactivate = () => {
    active.current = false;
    setForeground(false);
  };

  const getTarget = (clientX: number, clientY: number) => {
    if (!Number.isFinite(clientX) || !Number.isFinite(clientY)) return;

    const elements = document
      .elementsFromPoint(clientX, clientY)
      .filter((element) => element.getAttribute(CLICK_SHIELD_TARGET));

    if (elements.length && (elements[0] instanceof HTMLInputElement || elements[0] instanceof HTMLTextAreaElement)) {
      return elements[0];
    }
  };

  const handleClick = useCallback(
    (event: ClickOrTouchEvent) => {
      // Note: We intentionally do NOT stopPropagation here.
      // The shield intercepts clicks during capture phase, but parent handlers
      // (eg canvas selection) still need to receive the event.
      // The shielded input itself stops propagation when focused.
      onShieldClick?.();

      const { clientX, clientY } = getClientCoords(event);

      const target = getTarget(clientX, clientY);

      if (!active.current && target) {
        target.focus();
      }
    },
    [onShieldClick],
  );

  const handleDoubleClick = useCallback(
    (event: ClickOrTouchEvent) => {
      event.stopPropagation();
      onShieldDoubleClick?.();

      const { clientX, clientY } = getClientCoords(event);
      const target = getTarget(clientX, clientY);

      if (active.current && target) {
        target.focus();
        deactivate();
      }
    },
    [onShieldDoubleClick],
  );

  // note: ensure mobile safari will show the keyboard
  const handleSecondTouch: TouchEventHandler = (event) => {
    const clientX = event.changedTouches[0]?.clientX;
    const clientY = event.changedTouches[0]?.clientY;

    if (!clientX || !clientY) return;

    const target = getTarget(clientX, clientY);
    target?.focus();
  };

  const { onTouchStart, onTouchEnd } = useTouch({
    onFirstTouch: onShieldClick,
    onSecondTouch: handleSecondTouch,
    onDoubleTap: handleDoubleClick,
    options: { preventDefault: true },
  });

  return (
    <ClickShieldContext.Provider value={{ activate, deactivate }}>
      <ClickShieldContainer>
        {children}
        <ClickShield
          foreground={foreground}
          onClickCapture={handleClick}
          onDoubleClickCapture={handleDoubleClick}
          onTouchStartCapture={onTouchStart}
          onTouchEndCapture={onTouchEnd}
        />
      </ClickShieldContainer>
    </ClickShieldContext.Provider>
  );
};
